import React, { useEffect, useState } from 'react';
import { Lock, ArrowRight, Shield, LogOut, TrendingUp, BarChart3, Users, AlertCircle, RefreshCw, Calendar, Inbox, ExternalLink, Download, Code2, FileText } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Link } from 'react-router-dom';
import useInvestorTelemetry from '../hooks/useInvestorTelemetry';
import TrafficAnalytics from '../components/TrafficAnalytics';

/**
 * HiBFF — Active Investor Vault
 *
 * Per-investor passcode-gated surface. Endpoints (cookie-based):
 *   POST /api/investor-portal/login
 *   GET  /api/investor-portal/me                 — position + company (canonical)
 *   GET  /api/investor-portal/revenue-at-scale
 *   GET  /api/investor-portal/return-model       — forward IRR-style projections
 *   GET  /api/investor-portal/updates            — archived Monday pulses
 *   POST /api/investor-portal/data-room          — auto-create / fetch DR deep-link
 *   POST /api/investor-portal/logout
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n, dp = 0) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};
const fmtPct = (n) => n === null || n === undefined ? '—' : `${Number(n).toFixed(2)}%`;
const fmtUsers = (u) => u >= 1_000_000 ? `${(u / 1_000_000).toFixed(u % 1_000_000 ? 1 : 0)}M` : u >= 1000 ? `${(u / 1000).toFixed(0)}k` : String(u);

const PLAIN_ENGLISH = {
  safe: "Plain English: you've already paid for future shares. There's no interest and no repayment date — your money automatically becomes shares at the next funding round, at a discount for backing us early.",
  asa: "Plain English: you've pre-paid for shares that are issued at the next qualifying round (or the agreed longstop date). Structured to qualify for SEIS/EIS tax relief — no interest, by design.",
  cln: "Plain English: this is a loan that turns into shares. Interest builds up until the next funding round, when your loan plus interest converts into shares at a discount — or is repaid at maturity.",
};

const InvestorPortal = () => {
  const [view, setView] = useState('checking');     // checking | login | vault
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [data, setData] = useState(null);
  const [ras, setRas] = useState(null);
  const [returnModel, setReturnModel] = useState(null);
  const [updates, setUpdates] = useState(null);
  const [instruments, setInstruments] = useState(null);
  const [showWhitePaper, setShowWhitePaper] = useState(false);
  const [codeMetrics, setCodeMetrics] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [openingDR, setOpeningDR] = useState(false);
  const [drError, setDrError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { document.title = 'Active Investor Vault — HiBFF'; }, []);

  // Investor Vault telemetry — track dwell time on the portal once a logged-in
  // investor session is bound. `data.investor_id` lands after /me resolves.
  const portalInvestorId = (data && (data.investor_id || (data.investor && data.investor.investor_id))) || null;
  useInvestorTelemetry(portalInvestorId, 'investor-portal', view === 'vault' && !!portalInvestorId);

  // Cross-tab broadcast listener — when the founder hits "Save & broadcast"
  // in the Cap Table tab, this picks it up and refetches /me + return-model
  // so the live valuation block stays in lock-step across browser tabs.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'hibff_engine_v' && view === 'vault') {
        loadVault();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
     
  }, [view]);

  const loadVault = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/investor-portal/me`, { credentials: 'include' });
      if (r.status === 401 || r.status === 403) {
        setView('login');
        setData(null);
      } else if (r.ok) {
        const d = await r.json();
        setData(d);
        setView('vault');
        // Pull RAS, return-model, updates in parallel — all non-fatal
        try {
          const [r2, r3, r4, r5, r6, r7] = await Promise.all([
            fetch(`${BACKEND_URL}/api/investor-portal/revenue-at-scale`, { credentials: 'include' }),
            fetch(`${BACKEND_URL}/api/investor-portal/return-model`, { credentials: 'include' }),
            fetch(`${BACKEND_URL}/api/investor-portal/updates`, { credentials: 'include' }),
            fetch(`${BACKEND_URL}/api/investor-portal/instruments`, { credentials: 'include' }),
            fetch(`${BACKEND_URL}/api/investor-portal/codebase-metrics`, { credentials: 'include' }),
            fetch(`${BACKEND_URL}/api/investor-portal/traffic-analytics?days=30`, { credentials: 'include' }),
          ]);
          if (r2.ok) setRas(await r2.json());
          if (r3.ok) setReturnModel(await r3.json());
          if (r4.ok) setUpdates(await r4.json());
          if (r5.ok) setInstruments(await r5.json());
          if (r6.ok) setCodeMetrics(await r6.json());
          if (r7.ok) setTraffic(await r7.json());
        } catch (_e) {}
      } else {
        setView('login');
      }
    } catch (e) {
      setView('login');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { loadVault(); /* on first mount */ }, []);  

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    setLoggingIn(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/investor-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, passcode }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setLoginErr(j.detail || 'Sign-in failed.');
      } else {
        setPasscode('');
        await loadVault();
      }
    } catch (e) {
      setLoginErr('Network error — please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try { await fetch(`${BACKEND_URL}/api/investor-portal/logout`, { method: 'POST', credentials: 'include' }); } catch (_e) {}
    setData(null);
    setRas(null);
    setReturnModel(null);
    setUpdates(null);
    setView('login');
  };

  const handleOpenDataRoom = async () => {
    setOpeningDR(true);
    setDrError('');
    try {
      const r = await fetch(`${BACKEND_URL}/api/investor-portal/data-room`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setDrError(j.detail || 'Unable to open your Data Room.');
        return;
      }
      const j = await r.json();
      const url = j.deep_link_path || (j.passcode ? `/dataroom?code=${j.passcode}` : '');
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else setDrError('Data Room link unavailable.');
    } catch (_e) {
      setDrError('Network error opening Data Room.');
    } finally {
      setOpeningDR(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (view === 'checking') {
    return (
      <div className="min-h-screen bg-[#FFFDF9] text-[#1A1209] flex items-center justify-center">
        <RefreshCw className="animate-spin text-[#7B4FD8]" size={28} />
      </div>
    );
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  if (view === 'login') {
    return (
      <div className="min-h-screen bg-[#FFFDF9] text-[#1A1209] flex items-center justify-center px-4" data-testid="investor-portal-login">
        <PageMeta title="Active Investor Vault" path="/investor-vault" noIndex />
        <div className="w-full max-w-md">
          <Link to="/investor-portal" className="text-xs text-gray-500 hover:text-[#1A1209] inline-flex items-center gap-1.5 mb-6" data-testid="back-to-public-link">
            <ArrowRight size={12} className="rotate-180" /> Back to investor overview
          </Link>
          <div className="rounded-2xl border border-[#F3E8D9] bg-white shadow-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F5831F 0%, #FFB347 100%)' }}>
                <Lock size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Active Investor Vault</h1>
                <p className="text-xs text-gray-500">Passcode required — active investors only.</p>
              </div>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Investor email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-[#7B4FD8]"
                  data-testid="investor-portal-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Passcode</label>
                <input
                  type="password"
                  required
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-[#7B4FD8]"
                  data-testid="investor-portal-passcode"
                  autoComplete="current-password"
                />
              </div>
              {loginErr && (
                <div className="text-xs text-red-600 flex items-center gap-1.5" data-testid="investor-portal-error">
                  <AlertCircle size={14} /> {loginErr}
                </div>
              )}
              <button
                type="submit"
                disabled={loggingIn}
                className="w-full py-3 rounded-lg font-semibold text-sm text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #F5831F 0%, #FFB347 100%)' }}
                data-testid="investor-portal-submit"
              >
                {loggingIn ? 'Verifying…' : 'Enter Vault'}
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-6 leading-relaxed">
              Lost your passcode? Email <a href="mailto:invest@hibff.com" className="text-[#7B4FD8] hover:underline">invest@hibff.com</a> — we&apos;ll rotate it for you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Vault ─────────────────────────────────────────────────────────────────
  const inv = data?.investor || {};
  const co = data?.company || {};
  const live = data?.live_metrics || {};
  const dyn = data?.dynamic_valuation || {};
  return (
    <div className="min-h-screen bg-[#FFFDF9] text-[#1A1209]" data-testid="investor-portal-vault">
      {/* Top bar */}
      <header className="border-b border-[#F3E8D9] bg-white/85 backdrop-blur-sm px-6 lg:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-[#7B4FD8]" />
          <div className="text-sm">
            <div className="font-semibold">Active Investor Vault</div>
            <div className="text-xs text-gray-500">Welcome back, {inv.name || inv.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const r = await fetch(`${BACKEND_URL}/api/investor-portal/opportunity.pdf`, { credentials: 'include' });
                if (!r.ok) return;
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'HiBFF-Investor-Opportunity.pdf';
                a.click();
                URL.revokeObjectURL(url);
              } catch (_e) {}
            }}
            className="text-xs px-3 py-1.5 rounded-lg text-white bg-[#7B4FD8] hover:opacity-90 inline-flex items-center gap-1.5"
            title="Branded Opportunity Document — forward it to partners; clicks are attributed to you"
            data-testid="vault-opportunity-pdf-btn"
          >
            <Download size={12} /> Opportunity PDF
          </button>
          <button onClick={loadVault} disabled={refreshing} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="vault-refresh-btn">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={handleLogout} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5" data-testid="vault-logout-btn">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10 space-y-8">
        {/* ── Dynamic Valuation Engine — live ─────────────────────────────
            This block shows the investor the live company valuation and
            their own dynamic equity at that valuation. Reads directly from
            the Cap Table canonical config + dynamic engine. */}
        <section data-testid="vault-dynamic-engine">
          <div className="rounded-2xl p-6 bg-gradient-to-br from-[#F3EDFF] via-[#FAF6FF] to-[#FFF5EA] border border-[#E9DFF7]">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#7B4FD8] font-bold">Live Benchmark Valuation</div>
                <div className="text-3xl font-black mt-1" data-testid="vault-engine-ev">{fmtMoney(dyn.enterprise_valuation_usd)}</div>
                <div className="text-xs text-gray-600 mt-1">{co.valuation_basis || dyn.formula || ''}</div>
              </div>
              <div className="text-right">
                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  dyn.is_floor_active ? 'bg-[#FFF5EA] text-[#C2620A] border border-[#F5C99B]' : 'bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]'
                }`} data-testid="vault-engine-status">
                  {dyn.is_floor_active ? 'Floor active' : 'Revenue-scaled'}
                </span>
                <div className="text-[10px] text-gray-500 mt-2">Milestone {dyn.milestone_level ?? 0} · {dyn.milestone_label || '—'}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div className="bg-white/80 border border-[#EDE4F7] rounded-xl p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Live U</div><div className="text-lg font-bold">{(dyn.live_users ?? 0).toLocaleString()}</div></div>
              <div className="bg-white/80 border border-[#EDE4F7] rounded-xl p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">MRR</div><div className="text-lg font-bold">{fmtMoney(dyn.mrr_usd)}</div></div>
              <div className="bg-white/80 border border-[#EDE4F7] rounded-xl p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">ARR</div><div className="text-lg font-bold">{fmtMoney(dyn.arr_usd)}</div></div>
              <div className="bg-white/80 border border-[#EDE4F7] rounded-xl p-3"><div className="text-[10px] uppercase tracking-wider text-gray-500">Asset Floor</div><div className="text-lg font-bold">{fmtMoney(dyn.floor_usd)}</div></div>
            </div>
            <div className="border-t border-[#E9DFF7] pt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 text-xs text-gray-600 leading-relaxed">
                {dyn.is_floor_active
                  ? 'Live revenue × revenue-multiple is below the intrinsic asset floor — your equity is anchored to the asset-backed valuation.'
                  : 'Live revenue × revenue-multiple has surpassed the intrinsic asset floor — your equity is now scaling with growth.'}
              </div>
              <div className="bg-[#FFF5EA] border border-[#F5C99B] rounded-xl p-3 text-right" data-testid="vault-your-dynamic-equity">
                <div className="text-[10px] uppercase tracking-wider text-[#C2620A]">Your Live Equity</div>
                <div className="text-2xl font-black text-[#B45309]">{fmtMoney(inv.dynamic_equity_value_usd)}</div>
                <div className="text-[10px] text-[#C2620A]/80 mt-0.5">{fmtPct(inv.ownership_pct)} × V<sub>E</sub></div>
              </div>
            </div>
          </div>
        </section>

        {/* Investor position */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">Your Position</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Shares held" value={inv.shares?.toLocaleString() || '—'} testid="stat-shares" />
            <Stat label="Ownership" value={fmtPct(inv.ownership_pct)} testid="stat-ownership" />
            <Stat label="Live equity" value={fmtMoney(inv.dynamic_equity_value_usd)} hint={`@ ${fmtMoney(dyn.enterprise_valuation_usd)} live V_E`} testid="stat-indicative" />
            <Stat label="Invested" value={fmtMoney(inv.invested_amount_usd)} testid="stat-invested" />
          </div>
        </section>

        {/* Convertible instruments — SAFE / ASA / CLN held by this investor */}
        {(instruments?.instruments || []).length > 0 && (
          <section data-testid="vault-instruments-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500">Your Convertible Instruments</h2>
              <button
                onClick={async () => {
                  try {
                    const r = await fetch(`${BACKEND_URL}/api/investor-portal/instruments/guide.pdf`, { credentials: 'include' });
                    if (!r.ok) return;
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'HiBFF-Plain-English-Guide-SAFE-ASA-CLN.pdf';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (_e) {}
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="portal-guide-btn"
              >
                <ExternalLink size={12} /> Plain-English Guide (PDF)
              </button>
            </div>
            <div className="space-y-4">
              {instruments.instruments.map((it) => (
                <div key={it.instrument_id} className="rounded-2xl border border-[#D1EDE0] bg-[#F6FDF9] p-5" data-testid={`portal-instrument-${it.instrument_id}`}>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-2.5 py-1 rounded-lg text-[10px] font-black text-white"
                      style={{ background: it.type === 'safe' ? '#2563EB' : it.type === 'asa' ? '#7B4FD8' : '#B45309' }}>
                      {it.type?.toUpperCase()} · {it.country}
                    </span>
                    <span className="text-sm font-semibold text-gray-800">{it.label}</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      it.status === 'converted' ? 'bg-[#ECFDF5] text-[#047857]' : it.status === 'issued' ? 'bg-[#FFF5EA] text-[#C2620A]' : 'bg-gray-100 text-gray-500'
                    }`}>{it.status}</span>
                  </div>
                  {PLAIN_ENGLISH[it.type] && (
                    <p className="text-[11px] leading-relaxed text-gray-600 italic border-l-2 border-[#E5DFD3] pl-3 mb-4" data-testid={`portal-instrument-plain-${it.instrument_id}`}>
                      {PLAIN_ENGLISH[it.type]}
                    </p>
                  )}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-white border border-[#EDE7DC] rounded-xl p-3">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Invested</div>
                      <div className="text-lg font-bold">{fmtMoney(it.amount_usd)}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{it.discount_pct}% conversion discount{it.valuation_cap_usd ? ` · cap ${fmtMoney(it.valuation_cap_usd)}` : ''}</div>
                    </div>
                    <div className="bg-white border border-[#EDE7DC] rounded-xl p-3">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">You convert at</div>
                      <div className="text-lg font-bold">${Number(it.current?.conversion_price || 0).toFixed(2)}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">market ${Number(it.current?.share_price || 0).toFixed(2)}/share{it.type === 'cln' ? ` · ${it.interest_rate_pct || 0}% interest accruing` : ''}</div>
                    </div>
                    <div className="bg-white border border-[#EDE7DC] rounded-xl p-3">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Est. shares</div>
                      <div className="text-lg font-bold">{Number(it.current?.shares || 0).toLocaleString()}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">at today's valuation</div>
                    </div>
                    <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl p-3" data-testid={`portal-instrument-value-${it.instrument_id}`}>
                      <div className="text-[10px] uppercase tracking-wider text-[#047857]">Live conversion value</div>
                      <div className="text-lg font-black text-[#047857]">{fmtMoney(it.current?.value)}</div>
                      <div className="text-[10px] text-[#047857]/80 mt-0.5">{Number(it.current?.multiple || 0).toFixed(2)}x your money · updates with V<sub>E</sub></div>
                    </div>
                  </div>
                  {(it.projection || []).length > 1 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {it.projection.slice(0, 4).map((p) => (
                        <span key={p.users} className="text-[10px] px-2 py-1 rounded-full bg-[#F7F3EC] text-gray-600">
                          {fmtUsers(p.users)} users → <b className="text-[#1A1209]">{fmtMoney(p.value)}</b> ({p.multiple}x)
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Company snapshot */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">HiBFF Snapshot</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Valuation benchmark" value={fmtMoney(co.valuation_usd)} hint={co.valuation_basis} icon={TrendingUp} testid="stat-valuation" />
            <Stat label="Total shares" value={co.total_shares?.toLocaleString() || '—'} icon={Users} testid="stat-totalshares" />
            <Stat label="Total teens" value={live.total_users ?? '—'} icon={BarChart3} testid="stat-total-users" />
            <Stat label="MAU" value={live.mau_30d ?? '—'} icon={BarChart3} testid="stat-mau" />
          </div>
        </section>

        {/* Live Build Metrics — counted from the running system, provable on demand */}
        {codeMetrics && (
          <section data-testid="vault-build-metrics">
            <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">Live Build Metrics</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <Stat label="Lines of code" value={codeMetrics.total_lines?.toLocaleString() || '—'} hint={`${codeMetrics.backend_lines?.toLocaleString()} backend · ${codeMetrics.frontend_lines?.toLocaleString()} frontend`} icon={Code2} testid="stat-loc" />
              <Stat label="Live API endpoints" value={codeMetrics.live_endpoints?.toLocaleString() || '—'} hint={`${codeMetrics.api_route_files} route modules`} icon={Code2} testid="stat-endpoints" />
              <Stat label="Source files" value={((codeMetrics.backend_files || 0) + (codeMetrics.frontend_files || 0)).toLocaleString()} icon={Code2} testid="stat-source-files" />
              <Stat label="DB collections" value={codeMetrics.mongo_collections?.toLocaleString() || '—'} icon={Code2} testid="stat-collections" />
              <Stat label="Background workers" value={codeMetrics.worker_files ?? '—'} icon={Code2} testid="stat-workers" />
              <Stat label="Service modules" value={codeMetrics.service_files ?? '—'} icon={Code2} testid="stat-services" />
            </div>
            <p className="text-[11px] text-gray-500 mt-3" data-testid="build-metrics-timestamp">
              Counted live from the running system{codeMetrics.computed_at ? ` — verified ${new Date(codeMetrics.computed_at).toLocaleString()}` : ''}, not a brochure claim.
            </p>
          </section>
        )}

        {/* Live web traffic — first-party analytics, charts for investors */}
        {traffic && (
          <section data-testid="vault-traffic-analytics">
            <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">Live Web Traffic — last {traffic.days} days</h2>
            <TrafficAnalytics data={traffic} testidPrefix="ip-traffic" />
          </section>
        )}

        {/* Ownership baseline */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">Ownership Baseline</h2>
          <div className="rounded-2xl border border-[#F3E8D9] bg-white divide-y divide-[#F0EADF]" data-testid="ownership-baseline">
            {(Array.isArray(co.ownership_baseline) ? co.ownership_baseline : []).map((h) => (
              <div key={h.name} className="flex justify-between px-5 py-3 text-sm">
                <span className="text-gray-800">{h.name}{h.role ? <span className="text-gray-500 text-xs ml-2">{h.role}</span> : null}</span>
                <span className="font-semibold">{Number(h.pct).toFixed(0)}%</span>
              </div>
            ))}
            {(!co.ownership_baseline || co.ownership_baseline.length === 0) && (
              <div className="px-5 py-3 text-xs text-gray-500">No ownership baseline configured.</div>
            )}
          </div>
          {co.source_note && <p className="text-xs text-gray-600 mt-2">{co.source_note}</p>}
        </section>

        {/* Revenue at Scale */}
        {ras && (
          <section>
            <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-2">Revenue at Scale</h2>
            <p className="text-xs text-gray-500 mb-4">${ras.monthly_per_user_usd}/month per user. {ras.source}.</p>
            <div className="overflow-x-auto rounded-2xl border border-[#F3E8D9] bg-white">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-[#F0EADF]">
                  <tr><th className="text-left py-3 px-4">Users</th><th className="text-right py-3 px-4">Monthly</th><th className="text-right py-3 px-4">Annual</th></tr>
                </thead>
                <tbody>
                  {ras.anchors.map((row, i) => (
                    <tr key={row.users} className={i % 2 === 0 ? 'bg-[#FBF8F2]' : ''} data-testid={`vault-ras-${row.users}`}>
                      <td className="py-3 px-4 text-[#1A1209] font-medium">{fmtUsers(row.users)}</td>
                      <td className="py-3 px-4 text-right text-gray-600">{fmtMoney(row.monthly_revenue_usd)}</td>
                      <td className="py-3 px-4 text-right text-[#1A1209] font-semibold">{fmtMoney(row.annual_revenue_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Investor Return Model */}
        {returnModel && returnModel.anchors?.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-2">Return Model</h2>
            <p className="text-xs text-gray-500 mb-4">
              At ${returnModel.monthly_per_user_usd}/user/month × 12 months × {returnModel.revenue_multiple}× revenue multiple.
              Your projected value at each user anchor — based on your {returnModel.your_ownership_pct}% ownership.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-[#F3E8D9] bg-white">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-[#F0EADF]">
                  <tr>
                    <th className="text-left py-3 px-4">Users</th>
                    <th className="text-right py-3 px-4">Annual Revenue</th>
                    <th className="text-right py-3 px-4">Company Value</th>
                    <th className="text-right py-3 px-4">Your Value</th>
                    <th className="text-right py-3 px-4">×&nbsp;Invested</th>
                  </tr>
                </thead>
                <tbody>
                  {returnModel.anchors.map((row, i) => (
                    <tr key={row.users} className={i % 2 === 0 ? 'bg-[#FBF8F2]' : ''} data-testid={`vault-return-${row.users}`}>
                      <td className="py-3 px-4 text-[#1A1209] font-medium">{fmtUsers(row.users)}</td>
                      <td className="py-3 px-4 text-right text-gray-600">{fmtMoney(row.annual_revenue_usd)}</td>
                      <td className="py-3 px-4 text-right text-gray-600">{fmtMoney(row.indicative_company_value_usd)}</td>
                      <td className="py-3 px-4 text-right text-[#1A1209] font-semibold">{fmtMoney(row.your_indicative_value_usd)}</td>
                      <td className="py-3 px-4 text-right text-[#C2620A] font-semibold">
                        {row.your_multiplier_vs_invested ? `${row.your_multiplier_vs_invested}×` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-600 mt-3">{returnModel.disclaimer}</p>
          </section>
        )}

        {/* Pulse rhythm + Data Room CTA */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-[#F3E8D9] bg-white p-5 flex items-start gap-4" data-testid="vault-pulse-card">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F5831F 0%, #FFB347 100%)' }}>
              <Calendar size={18} className="text-white" />
            </div>
            <div className="text-sm">
              <div className="font-semibold mb-1">HiBFF Investor Pulse</div>
              <div className="text-gray-600">Mondays @ 09:00 UTC — delivered to {inv.email || 'your inbox'}. This Vault is the deep-dive destination behind every pulse.</div>
            </div>
          </div>
          <div className="rounded-2xl border border-[#D1EDE0] bg-[#F6FDF9] p-5 flex items-start gap-4" data-testid="vault-dataroom-card">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#DCFCE7]">
              <ExternalLink size={18} className="text-[#047857]" />
            </div>
            <div className="flex-1">
              <div className="font-semibold mb-1">Your Personal Data Room</div>
              <div className="text-gray-600 text-sm mb-3">Permanent investor-tier Data Room with live metrics, board decks, and curated materials. Opens in a new tab.</div>
              <button
                onClick={handleOpenDataRoom}
                disabled={openingDR}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#0F8A4B] hover:opacity-90 text-white disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid="open-dataroom-btn"
              >
                {openingDR ? 'Opening…' : (<>Open Data Room <ArrowRight size={12} /></>)}
              </button>
              {drError && <p className="text-xs text-red-600 mt-2" data-testid="dataroom-open-error">{drError}</p>}
            </div>
          </div>
        </section>

        {/* White Paper — Warm Handover safety standard (founder request Jun 2026) */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">White Paper</h2>
          <div className="rounded-2xl border border-[#E9E2F7] bg-[#FBFAFE] p-5" data-testid="vault-whitepaper-card">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#EDE9FE] shrink-0">
                <FileText size={18} className="text-[#6D28D9]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold mb-1">Warm Handover — A Safety Standard for AI Conversations with Young People</div>
                <div className="text-gray-600 text-sm mb-3">HiBFF&apos;s safety white paper: why AI companions for teens need a deterministic human handover, and how HiBFF&apos;s protocol delivers it. 10 pages.</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowWhitePaper((v) => !v)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#6D28D9] hover:opacity-90 text-white inline-flex items-center gap-1.5"
                    data-testid="whitepaper-read-btn"
                  >
                    {showWhitePaper ? 'Hide reader' : (<>Read on screen <ArrowRight size={12} /></>)}
                  </button>
                  <a
                    href="/docs/HiBFF_Warm_Handover_White_Paper.pdf"
                    download="HiBFF_Warm_Handover_White_Paper.pdf"
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#6D28D9] text-[#6D28D9] hover:bg-[#EDE9FE] inline-flex items-center gap-1.5"
                    data-testid="whitepaper-download-btn"
                  >
                    Download PDF <Download size={12} />
                  </a>
                </div>
              </div>
            </div>
            {showWhitePaper && (
              <div className="mt-4 rounded-xl overflow-hidden border border-[#E9E2F7]" data-testid="whitepaper-viewer">
                <iframe
                  src="/docs/HiBFF_Warm_Handover_White_Paper.pdf#view=FitH"
                  title="Warm Handover White Paper"
                  className="w-full"
                  style={{ height: '75vh' }}
                />
                <div className="px-4 py-2 text-xs text-gray-500 bg-white border-t border-[#E9E2F7]">
                  Reader not loading? <a href="/docs/HiBFF_Warm_Handover_White_Paper.pdf" target="_blank" rel="noopener noreferrer" className="text-[#6D28D9] underline" data-testid="whitepaper-newtab-link">Open the PDF in a new tab</a>.
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Investor Updates archive */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-4">Investor Updates</h2>
          <div className="rounded-2xl border border-[#F3E8D9] bg-white" data-testid="vault-updates-archive">
            {(!updates || updates.count === 0) ? (
              <div className="px-5 py-6 text-sm text-gray-500 flex items-start gap-3" data-testid="vault-updates-empty">
                <Inbox size={16} className="text-gray-400 mt-0.5" />
                <div>
                  <div className="text-gray-800 font-medium mb-0.5">No pulses delivered yet.</div>
                  <div className="text-xs text-gray-500">Pulses are sent {updates?.rhythm || 'Mondays @ 09:00 UTC'} and will appear here.</div>
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-[#F0EADF]">
                {updates.entries.map((u) => (
                  <li key={u.pulse_id} className="px-5 py-3 flex items-center justify-between gap-4" data-testid={`pulse-row-${u.pulse_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-[#1A1209] truncate">{u.subject || `Pulse — ${u.week}`}</div>
                      <div className="text-xs text-gray-500">{u.sent_at ? new Date(u.sent_at).toLocaleString() : ''}</div>
                    </div>
                    <button
                      onClick={() => {
                        const w = window.open('', '_blank', 'noopener,noreferrer');
                        if (w) {
                          w.document.open();
                          w.document.write(u.html || '<p>Empty pulse.</p>');
                          w.document.close();
                        }
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5"
                      data-testid={`pulse-read-${u.pulse_id}`}
                    >
                      Read <ArrowRight size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <p className="text-xs text-gray-600 mt-12">
          All figures shown are indicative and reflect the founder-document baseline. Last refreshed {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}.
        </p>
      </main>
    </div>
  );
};

const Stat = ({ label, value, hint, icon: Icon, testid }) => (
  <div className="rounded-2xl border border-[#F3E8D9] bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      {Icon && <Icon size={14} className="text-gray-400" />}
    </div>
    <div className="text-2xl font-bold mb-1">{value}</div>
    {hint && <div className="text-xs text-gray-500">{hint}</div>}
  </div>
);

export default InvestorPortal;
